import { FC, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import Modal from 'components/layouts/modal';
import { AccessRequest } from 'types/access-request';
import useApi from 'utils/use-api';
import { useAuthStore } from 'state/auth';
import dayjs from 'dayjs';
import wrapClick from 'utils/wrap-click';
import {
  CheckCircleIcon,
  XCircleIcon,
  QuestionMarkCircleIcon,
  ArrowPathIcon,
  ClockIcon,
  ChatBubbleLeftIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import ReminderModal from 'components/forms/access-request/reminder-modal';
import ReassignModal from './reassign';
import RequestMoreInfoModal from './request';
import ReviewModal from 'components/forms/access-request/review-modal';

interface ViewRequestModalProps {
  open: boolean;
  setOpen: (val: boolean) => void;
  request: AccessRequest;
  onSuccess: () => void;
}

const statusColors = {
  open: 'bg-blue-50 text-blue-700 ring-blue-600/20',
  reopened: 'bg-purple-50 text-purple-700 ring-purple-600/20',
  processing: 'bg-yellow-50 text-yellow-700 ring-yellow-600/20',
  more_info: 'bg-orange-50 text-orange-700 ring-orange-600/20',
  awaiting_approval: 'bg-cyan-50 text-cyan-700 ring-cyan-600/20',
  complete: 'bg-green-50 text-green-700 ring-green-600/20',
  closed: 'bg-gray-50 text-gray-700 ring-gray-600/20',
};

const statusLabels = {
  open: 'Open',
  reopened: 'Re-Opened',
  processing: 'Processing',
  more_info: 'More Info Needed',
  awaiting_approval: 'Awaiting Approval',
  complete: 'Complete',
  closed: 'Closed',
};

const categoryLabels: Record<string, string> = {
  hardware: 'Hardware',
  system_access: 'System Access',
  software: 'Software',
  email: 'Email Account',
  communication: 'Communication Tools',
  training: 'Training Access',
  other: 'Other',
};

const ViewRequestModal: FC<ViewRequestModalProps> = ({ open, setOpen, request, onSuccess }) => {
  const api = useApi();
  const currentUser = useAuthStore(state => state.user);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showMoreInfoModal, setShowMoreInfoModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'activity'>('details');
  const [newComment, setNewComment] = useState('');

  const { data: requestDetails, refetch } = useQuery<AccessRequest>({
    queryKey: ['access-request', request.id],
    queryFn: async () => {
      const response = await api.get(`/access-requests/${request.id}`);
      return response.data;
    },
    initialData: request,
  });

  const { data: reminders, refetch: refetchReminders } = useQuery({
    queryKey: ['access-request-reminders', request.id],
    queryFn: async () => {
      const response = await api.get(`/access-requests/${request.id}/reminders`);
      return response.data || [];
    },
  });

  const { data: activity, refetch: refetchActivity } = useQuery({
    queryKey: ['access-request-activity', request.id],
    queryFn: async () => {
      const response = await api.get(`/access-requests/${request.id}/history`);
      return response.data || [];
    },
  });

  const { data: review, refetch: refetchReview } = useQuery({
    queryKey: ['access-request-review', request.id],
    queryFn: async () => {
      const response = await api.get(`/access-requests/${request.id}/review`);
      return response.data;
    },
  });

  const addCommentMutation = useMutation({
    mutationFn: async (message: string) => {
      const response = await api.post(`/access-requests/${request.id}/comments`, { message });
      return response.data;
    },
    onSuccess: () => {
      setNewComment('');
      refetchActivity();
      onSuccess();
    },
  });

  const approveRequestMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/access-requests/${request.id}/approve`);
      return response.data;
    },
    onSuccess: () => {
      refetch();
      onSuccess();
      refetchActivity();
      refetchReminders();
      setShowReviewModal(true);
    },
  });

  const rejectRequestMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/access-requests/${request.id}/reject`);
      return response.data;
    },
    onSuccess: () => {
      refetch();
      onSuccess();
      refetchActivity();
    },
  });

  const reopenRequestMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/access-requests/${request.id}/reopen`);
      return response.data;
    },
    onSuccess: () => {
      refetch();
      onSuccess();
      refetchActivity();
    },
  });

  const markProcessingMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/access-requests/${request.id}/processing`);
      return response.data;
    },
    onSuccess: () => {
      refetch();
      onSuccess();
      refetchActivity();
      refetchReminders();
    },
  });

  const markCompleteMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/access-requests/${request.id}/complete`);
      return response.data;
    },
    onSuccess: () => {
      refetch();
      onSuccess();
      refetchActivity();
    },
  });

  const formatDate = (dateString: string) => {
    try {
      return dayjs(dateString).format('MMM DD, YYYY HH:mm');
    } catch {
      return dateString;
    }
  };

  const isRequester = currentUser?.id === requestDetails.requesterId || currentUser?.email === requestDetails.requesterEmail;
  const isAssignee = true;

  const canMarkComplete = isAssignee && ['open', 'reopened', 'processing', 'more_info'].includes(requestDetails.status);

  const canApprove = isRequester && requestDetails.status === 'awaiting_approval' && requestDetails.completedByAssignee;

  const canApproveReject =
    isAssignee && ['open', 'reopened', 'processing', 'more_info'].includes(requestDetails.status) && !requestDetails.completedByAssignee;

  const canReopen = isRequester && requestDetails.status === 'closed';
  const canMarkProcessing = isAssignee && ['open', 'reopened'].includes(requestDetails.status);

  return (
    <>
      <Modal
        open={open}
        setOpen={setOpen}
        title={`Request #${request.id}`}
        size="7xl"
        status={() => (
          <span
            className={`inline-flex items-center rounded-md px-3 py-1 text-sm font-medium ring-1 ring-inset ${statusColors[requestDetails.status]}`}
          >
            {statusLabels[requestDetails.status]}
          </span>
        )}
        renderActionsTop={() => (
          <div className="mr-14">
            {canApprove && (
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={wrapClick(() => approveRequestMutation.mutate())}
                  disabled={approveRequestMutation.isPending}
                  className="flex-1 inline-flex items-center justify-center gap-x-2 rounded-lg bg-green-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-green-500 transition-colors disabled:opacity-50"
                >
                  <CheckCircleIcon className="h-5 w-5" aria-hidden="true" />
                  Approve
                </button>
                <button
                  type="button"
                  onClick={wrapClick(() => rejectRequestMutation.mutate())}
                  disabled={rejectRequestMutation.isPending}
                  className="flex-1 inline-flex items-center justify-center gap-x-2 rounded-lg bg-red-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-red-500 transition-colors disabled:opacity-50"
                >
                  <XCircleIcon className="h-5 w-5" aria-hidden="true" />
                  Reject
                </button>
              </div>
            )}

            {isAssignee && !canApprove && (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  {canMarkProcessing && (
                    <button
                      type="button"
                      onClick={wrapClick(() => markProcessingMutation.mutate())}
                      disabled={markProcessingMutation.isPending}
                      className="inline-flex items-center gap-x-1.5 rounded-lg border border-yellow-600 px-3 py-2 text-sm font-semibold text-yellow-600 shadow-sm hover:bg-yellow-100 transition-colors disabled:opacity-50"
                    >
                      <ArrowPathIcon className="h-4 w-4" />
                      Process
                    </button>
                  )}
                  {canMarkComplete && (
                    <button
                      type="button"
                      onClick={wrapClick(() => markCompleteMutation.mutate())}
                      disabled={markCompleteMutation.isPending}
                      className="inline-flex items-center gap-x-1.5 rounded-lg bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 transition-colors disabled:opacity-50"
                    >
                      <CheckCircleIcon className="h-4 w-4" />
                      Mark Complete
                    </button>
                  )}
                  {canApproveReject && (
                    <>
                      <button
                        type="button"
                        onClick={wrapClick(() => setShowMoreInfoModal(true))}
                        className="inline-flex items-center gap-x-1.5 rounded-lg border border-orange-600 px-3 py-2 text-sm font-semibold text-orange-600 shadow-sm hover:bg-orange-100 transition-colors"
                      >
                        <QuestionMarkCircleIcon className="h-4 w-4" />
                        Request Info
                      </button>
                      <button
                        type="button"
                        onClick={wrapClick(() => setShowReassignModal(true))}
                        className="inline-flex items-center gap-x-1.5 rounded-lg border border-blue-600 px-3 py-2 text-sm font-semibold text-blue-600 shadow-sm hover:bg-blue-100 transition-colors"
                      >
                        <ArrowPathIcon className="h-4 w-4" />
                        Reassign
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {canReopen && (
              <button
                type="button"
                onClick={wrapClick(() => reopenRequestMutation.mutate())}
                disabled={reopenRequestMutation.isPending}
                className="w-full inline-flex items-center justify-center gap-x-2 rounded-lg bg-purple-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-purple-100 transition-colors disabled:opacity-50"
              >
                <ArrowPathIcon className="h-5 w-5" />
                Reopen Request
              </button>
            )}
          </div>
        )}
      >
        <div className="space-y-5">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('details')}
                className={`${
                  activeTab === 'details'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium`}
              >
                Details
              </button>
              <button
                onClick={() => setActiveTab('activity')}
                className={`${
                  activeTab === 'activity'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium inline-flex items-center gap-x-2`}
              >
                <ChatBubbleLeftIcon className="h-4 w-4" />
                Activity
              </button>
            </nav>
          </div>

          {activeTab === 'details' ? (
            <div className="space-y-5">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <dl className="space-y-4">
                  <div className="border-b border-gray-200 pb-4">
                    <dt className="text-sm font-medium text-gray-500">Requester</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      <div className="flex flex-col">
                        <span className="font-medium">{requestDetails.requesterName}</span>
                        <span className="text-gray-500">{requestDetails.requesterEmail}</span>
                      </div>
                    </dd>
                  </div>

                  <div className="border-b border-gray-200 pb-4">
                    <dt className="text-sm font-medium text-gray-500">Category</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
                        {categoryLabels[requestDetails.category] || requestDetails.category}
                      </span>
                    </dd>
                  </div>

                  <div className="border-b border-gray-200 pb-4">
                    <dt className="text-sm font-medium text-gray-500">Details</dt>
                    <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{requestDetails.details}</dd>
                  </div>

                  <div className="border-b border-gray-200 pb-4">
                    <dt className="text-sm font-medium text-gray-500">Assigned To</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      <div className="flex items-center gap-x-2">
                        <span className="font-medium">{requestDetails.assigneeName}</span>
                        <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                          {requestDetails.assigneeType === 'group' ? 'Group' : 'User'}
                        </span>
                      </div>
                    </dd>
                  </div>

                  <div className="border-b border-gray-200 pb-4">
                    <dt className="text-sm font-medium text-gray-500">Created</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formatDate(requestDetails.createdAt)}</dd>
                  </div>

                  {requestDetails.completedByAssignee && requestDetails.completedAt && (
                    <div className="border-b border-gray-200 pb-4">
                      <dt className="text-sm font-medium text-gray-500">Completed by Assignee</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        <div className="flex items-center gap-x-2">
                          <CheckCircleIcon className="h-5 w-5 text-green-600" />
                          <span>{formatDate(requestDetails.completedAt)}</span>
                          {requestDetails.status === 'awaiting_approval' && (
                            <span className="inline-flex items-center rounded-md bg-cyan-50 px-2 py-1 text-xs font-medium text-cyan-700 ring-1 ring-inset ring-cyan-700/10">
                              Awaiting Your Approval
                            </span>
                          )}
                        </div>
                      </dd>
                    </div>
                  )}

                  {requestDetails.status === 'closed' && isRequester && (
                    <div className="pb-4">
                      <dt className="text-sm font-medium text-gray-500 mb-2">Service Review</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {review ? (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <div className="flex items-center gap-x-2 mb-2">
                              {[1, 2, 3, 4, 5].map(star => (
                                <StarIcon
                                  key={star}
                                  className={`h-5 w-5 ${star <= review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                                />
                              ))}
                              <span className="text-sm font-medium text-gray-700">({review.rating}/5)</span>
                            </div>
                            {review.reviewText && <p className="text-sm text-gray-700 mt-2">{review.reviewText}</p>}
                            <p className="text-xs text-gray-500 mt-2">Submitted on {formatDate(review.createdAt)}</p>
                          </div>
                        ) : requestDetails.completedByAssignee ? (
                          <button
                            type="button"
                            onClick={wrapClick(() => setShowReviewModal(true))}
                            className="inline-flex items-center gap-x-1.5 rounded-md bg-yellow-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-yellow-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-600"
                          >
                            <StarIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
                            Rate this Service
                          </button>
                        ) : (
                          <p className="text-sm text-gray-500">This request was closed without completion by an assignee.</p>
                        )}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <ClockIcon className="h-5 w-5 text-primary-600" />
                    <h3 className="text-sm font-semibold text-gray-900">Reminders</h3>
                  </div>
                  <button
                    type="button"
                    onClick={wrapClick(() => setShowReminderModal(true))}
                    className="text-sm font-medium text-primary-600 hover:text-primary-500"
                  >
                    + Add
                  </button>
                </div>
                <ul className="space-y-3 max-h-48 overflow-auto pr-2">
                  {reminders && reminders.length > 0 ? (
                    reminders.map((rem: any) => (
                      <li key={rem.id} className="flex items-center justify-between rounded-md border border-gray-200 p-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{formatDate(rem.reminderDate)}</p>
                          {rem.message && <p className="text-sm text-gray-600">{rem.message}</p>}
                        </div>
                        <button
                          type="button"
                          onClick={async () => {
                            await api.delete(`/access-requests/${request.id}/reminders/${rem.id}`);
                            refetchReminders();
                            refetchActivity();
                          }}
                          className="text-sm text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      </li>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No reminders set.</p>
                  )}
                </ul>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Activity Feed</h3>
                <ul className="space-y-4 max-h-96 overflow-auto pr-2">
                  {activity && activity.length > 0 ? (
                    activity.map((item: any) => (
                      <li key={item.id} className="flex gap-3">
                        {item.type === 'system' ? (
                          <>
                            <div className="mt-1 h-2 w-2 rounded-full bg-primary-500 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900 capitalize">{item.action}</p>
                              {item.note && <p className="text-sm text-gray-600">{item.note}</p>}
                              <p className="text-xs text-gray-500">{formatDate(item.createdAt)}</p>
                            </div>
                          </>
                        ) : (
                          <>
                            <ChatBubbleLeftIcon className="mt-1 h-5 w-5 text-blue-500 flex-shrink-0" />
                            <div className="flex-1">
                              <div className="bg-gray-50 rounded-lg p-3">
                                <p className="text-sm font-medium text-gray-900">
                                  {item.user?.firstName} {item.user?.lastName}
                                </p>
                                <p className="text-sm text-gray-700 mt-1">{item.message}</p>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">{formatDate(item.createdAt)}</p>
                            </div>
                          </>
                        )}
                      </li>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No activity yet.</p>
                  )}
                </ul>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Add Comment</h3>
                <div className="space-y-3">
                  <textarea
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    placeholder="Write a comment..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (newComment.trim()) {
                        addCommentMutation.mutate(newComment);
                      }
                    }}
                    disabled={!newComment.trim() || addCommentMutation.isPending}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {addCommentMutation.isPending ? 'Posting...' : 'Post Comment'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {showReminderModal && (
        <ReminderModal
          open={showReminderModal}
          setOpen={setShowReminderModal}
          requestId={request.id}
          onSuccess={() => {
            setShowReminderModal(false);
            refetchActivity();
            refetch();
          }}
        />
      )}

      {showReassignModal && (
        <ReassignModal
          open={showReassignModal}
          setOpen={setShowReassignModal}
          requestId={request.id}
          onSuccess={() => {
            setShowReassignModal(false);
            refetch();
            onSuccess();
          }}
        />
      )}

      {showMoreInfoModal && (
        <RequestMoreInfoModal
          open={showMoreInfoModal}
          setOpen={setShowMoreInfoModal}
          requestId={request.id}
          onSuccess={() => {
            setShowMoreInfoModal(false);
            refetch();
            onSuccess();
          }}
        />
      )}

      {showReviewModal && (
        <ReviewModal
          open={showReviewModal}
          setOpen={setShowReviewModal}
          requestId={request.id}
          assigneeName={requestDetails.assigneeName}
          onSuccess={() => {
            setShowReviewModal(false);
            refetchReview();
            refetchActivity();
          }}
        />
      )}
    </>
  );
};

export default ViewRequestModal;
